import {
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { cn } from "@/lib/utils";
import Loader from "../SVG/Loader";
import frames from "./frames";
import { MATIC_360 } from "./matic360Content";
import { type FrameFit, use360Viewer } from "./use360Viewer";

const SLIDER_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

type Matic360ViewerProps = {
  enabled: boolean;
  focusOnReady?: boolean;
  /** Draws "DRAG TO ROTATE" along the bottom edge. */
  hint?: boolean;
  fit?: FrameFit;
};

// The turntable content (canvas + loader + drag) that fills its parent frame,
// with no modal shell — shared by the standalone 360 modal and the gallery
// lightbox slide.
const Matic360Viewer = ({
  enabled,
  focusOnReady = false,
  hint = true,
  fit = "cover",
}: Matic360ViewerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { frame, isDragging, canvasReady } = use360Viewer({
    frames,
    canvasRef,
    enabled,
    hint,
    fit,
  });

  useEffect(() => {
    if (focusOnReady && canvasReady) {
      canvasRef.current?.focus({ preventScroll: true });
    }
  }, [focusOnReady, canvasReady]);

  // The turn so far, in whole degrees, for assistive tech.
  const degrees = Math.round((frame * 360) / frames.length);

  // The hook turns the frames; this only keeps the slider keys from also
  // reaching the lightbox's slide nav.
  const handleCanvasKeyDown = (
    event: ReactKeyboardEvent<HTMLCanvasElement>,
  ) => {
    if (SLIDER_KEYS.has(event.key)) event.stopPropagation();
  };

  return (
    <>
      {!canvasReady && (
        <>
          <img
            src={frames[0]}
            alt=""
            className={cn(
              "absolute inset-0 h-full w-full",
              fit === "cover" && "object-cover",
              fit === "contain" && "object-contain",
              // Untrimmed, so the robot sits a little high until the
              // canvas takes over.
              fit === "stand" && "object-contain object-bottom",
            )}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader />
          </div>
        </>
      )}
      <canvas
        ref={canvasRef}
        role="slider"
        tabIndex={0}
        // Shown while the frames load, so it stays focusable, announced (as
        // busy) and ringed when focused; until the first frame draws it is
        // blank, and the poster and loader show through. Only sideways drags
        // turn it, so a vertical swipe still scrolls the page.
        className={cn(
          "absolute inset-0 h-full w-full touch-pan-y outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-brand-black",
          isDragging ? "cursor-grabbing" : "cursor-grab",
        )}
        aria-label={MATIC_360.label}
        aria-busy={!canvasReady}
        aria-valuemin={0}
        aria-valuemax={359}
        aria-valuenow={degrees}
        aria-valuetext={MATIC_360.turned(degrees)}
        onKeyDown={handleCanvasKeyDown}
      />
    </>
  );
};

export default Matic360Viewer;
